import React from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Alert, alpha, Box, Button, Card, CardContent, IconButton, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'

import { TripListCategory } from '@/src/types/trips'
import { TripListCard as TripListCardComponent } from '@/src/components/common/trips'
import { TripTitleParts } from '@/src/utils/trips/tripListHelpers'

export type TripStatusTone = 'success' | 'error' | 'warning' | 'default'

export interface TripListCard {
    id: string
    tripId?: string
    titleParts: TripTitleParts
    subtitle?: string
    bookedByLabel?: string
    bookingSource?: 'MAIN' | 'LINKED'
    dateDay?: string
    dateMonth?: string
    dateLabel?: string
    statusLabel?: string
    statusTone: TripStatusTone
}

export interface TripListTabs {
    value: TripListCategory
    label: string
    count?: number
}

interface TripListProps {
    heading: string
    isRtl: boolean
    hideHeader?: boolean
    onBack: () => void
    tabs: TripListTabs[]
    activeTab: TripListCategory
    onTabChange: (tab: TripListCategory) => void
    cards: TripListCard[]
    isLoading: boolean
    isError: boolean
    errorLabel?: string
    retryLabel?: string
    onRetry: () => void
    emptyState: {
        title: string
        description: string
        actionLabel: string
        onAction: () => void
        subheading?: string
    }
    showSignInCta: boolean
    signInCtaLabel: string
    onSignIn: () => void
    onCardClick?: (tripId: string, bookingSource?: 'MAIN' | 'LINKED') => void
    hasMore?: boolean
    isLoadingMore?: boolean
    onLoadMore?: () => void
    loadMoreLabel?: string
}

const SkeletonCard: React.FC = () => (
    <Card elevation={0} sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex' }}>
                <Box
                    sx={{
                        minWidth: 44,
                        pr: 1.5,
                        mr: 1.5,
                        borderRight: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Skeleton variant="text" width={24} height={26} />
                    <Skeleton variant="text" width={24} height={12} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Skeleton variant="text" width="55%" height={20} />
                        <Skeleton variant="rounded" width={64} height={20} />
                    </Stack>
                    <Skeleton variant="text" width="70%" height={16} sx={{ mt: 0.5 }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
)

const TripListSkeleton: React.FC = () => (
    <Stack spacing={1.25}>
        {Array.from({ length: 4 }).map((_, idx) => (
            <SkeletonCard key={idx} />
        ))}
    </Stack>
)

const LoadMoreShimmer: React.FC = () => (
    <Stack spacing={1.25} sx={{ mt: 1.25 }}>
        {Array.from({ length: 2 }).map((_, idx) => (
            <SkeletonCard key={idx} />
        ))}
    </Stack>
)

const EmptyState: React.FC<{
    title: string
    description: string
    actionLabel: string
    onAction: () => void
    isApp: boolean
}> = ({ title, description, actionLabel, onAction, isApp }) => (
    <Box
        sx={{
            textAlign: 'center',
            py: 3,
            px: 2,
            bgcolor: 'background.paper',
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider'
        }}
    >
        <Typography
            variant="subtitle1"
            sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: '1rem'
            }}
        >
            {title}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                color: 'text.secondary',
                maxWidth: 320,
                mx: 'auto',
                mb: !isApp ? 2.5 : 0,
                fontSize: '0.875rem',
                lineHeight: 1.5
            }}
        >
            {description}
        </Typography>
        {actionLabel && !isApp && (
            <Button
                variant="contained"
                size="medium"
                onClick={onAction}
                sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem'
                }}
            >
                {actionLabel}
            </Button>
        )}
    </Box>
)

export const TripList: React.FC<TripListProps> = ({
    heading,
    isRtl,
    hideHeader = false,
    onBack,
    tabs,
    activeTab,
    onTabChange,
    cards,
    isLoading,
    isError,
    errorLabel,
    retryLabel,
    onRetry,
    emptyState,
    showSignInCta,
    signInCtaLabel,
    onSignIn,
    onCardClick,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    loadMoreLabel = 'Load more'
}) => {
    return (
        <Box
            sx={{
                minHeight: '100dvh',
                bgcolor: 'background.default',
                pb: 10
            }}
        >
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
            >
                {!hideHeader && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.5, position: 'relative' }}
                    >
                        <IconButton aria-label={heading} onClick={onBack} sx={{ ml: -1 }}>
                            <ArrowBackIcon sx={{ fontSize: 20, transform: isRtl ? 'scaleX(-1)' : 'none' }} />
                        </IconButton>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: '16px',
                                color: 'text.primary',
                                textAlign: 'center',
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)'
                            }}
                        >
                            {heading}
                        </Typography>
                        <Box sx={{ width: 40 }} />
                    </Stack>
                )}

                {/* Tabs - underlined, same as desktop */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => onTabChange(newValue)}
                        sx={{
                            minHeight: 40,
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.875rem',
                                minHeight: 40,
                                px: 1.5,
                                py: 0,
                                color: 'text.secondary',
                                '&.Mui-selected': theme => ({
                                    fontWeight: 600,
                                    color: alpha(theme.palette.link.main, 0.9)
                                })
                            },
                            '& .MuiTabs-indicator': {
                                height: 2.5,
                                borderRadius: '2px 2px 0 0',
                                backgroundColor: theme => alpha(theme.palette.link.main, 0.9)
                            }
                        }}
                    >
                        {tabs.map(tab => (
                            <Tab key={tab.value} value={tab.value} label={tab.label} />
                        ))}
                    </Tabs>
                </Box>
            </Box>

            <Box sx={{ px: 2, py: 1.5 }}>
                {isLoading && cards.length === 0 && <TripListSkeleton />}

                {!isLoading && isError && (
                    <Alert
                        severity="error"
                        sx={{
                            borderRadius: 1.5,
                            border: 1,
                            borderColor: 'divider',
                            mb: 2,
                            fontSize: '0.875rem'
                        }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={onRetry}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.8125rem',
                                    fontWeight: 500
                                }}
                            >
                                {retryLabel || errorLabel}
                            </Button>
                        }
                    >
                        {errorLabel}
                    </Alert>
                )}

                {!isLoading && !isError && cards.length === 0 && (
                    <>
                        <EmptyState
                            title={emptyState.title}
                            description={emptyState.description}
                            actionLabel={emptyState.actionLabel}
                            onAction={emptyState.onAction}
                            isApp={hideHeader}
                        />
                    </>
                )}

                {!isError && cards.length > 0 && (
                    <>
                        <Stack spacing={1.25}>
                            {cards.map(card => (
                                <TripListCardComponent key={card.id} {...card} onClick={onCardClick} />
                            ))}
                        </Stack>
                        {isLoadingMore && <LoadMoreShimmer />}
                        {hasMore && onLoadMore && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={onLoadMore}
                                    disabled={isLoadingMore}
                                    sx={{
                                        minWidth: 120,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 3,
                                        py: 1,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {isLoadingMore ? 'Loading...' : loadMoreLabel}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

export type { TripListProps }
